import React, { useState, useEffect, useContext } from 'react';
import { Users, Plus, Search, UserPlus } from 'lucide-react';

import { ThemeContext } from "../../constant/theme";
import { LanguageContext } from "../../constant/LanguageContext";

import { text } from './constants/translations';
import { getGroupStyles } from './styles/groupStyles';

import useResponsive from './hooks/useResponsive';
import useGroups from './hooks/useGroups';
import useEmployees from './hooks/useEmployees';
import { useProfile } from '../../hooks/useProfile';
import { useAuth } from '../../context/AuthContext';

import AlertModal from '../../components/AlertModal';
import GroupCard from './components/GroupCard';
import CreateGroupModal from './components/CreateGroupModal';
import AssignModal from './components/AssignModal';
import GroupDetailsModal from './components/GroupDetailsModal';
import { SkeletonStyles, SkeletonBox, LoadingPill } from "../../components/SkeletonLoader";

function GroupsManagement() {
  const { theme } = useContext(ThemeContext);
  const { language } = useContext(LanguageContext);
  const { profile } = useProfile();
  const { hasFeatureAccess } = useAuth();

  // Only applies to promoted users browsing in Admin View; normal user/manager
  // views keep full access — same pattern as MessengerPage's canCreateGroup.
  // The "groups" registry entry previously didn't exist, so a superadmin had
  // no way to grant/restrict these buttons for a promoted admin-level account.
  const isAdminViewGated = (localStorage.getItem('managerViewType') || localStorage.getItem('viewType') || 'user') === 'admin';
  const canCreateGroup = !isAdminViewGated || hasFeatureAccess('groups', 'create');
  const canAssignEmployees = !isAdminViewGated || hasFeatureAccess('groups', 'assignEmployees');

  const { screenSize, spacing, fonts } = useResponsive();

  const [userRole, setUserRole] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("member");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedGroupDetails, setSelectedGroupDetails] = useState(null);
  const [hoveredTab, setHoveredTab] = useState(null);

  const {
    groups,
    allGroups,
    createdGroups,
    loading,
    error,
    success,
    setError,
    setSuccess,
    fetchMemberOfGroups,
    fetchAllGroups,
    fetchCreatedGroups,
    handleCreateGroup,
    handleAssignEmployees,
    fetchGroupMembers,
  } = useGroups(userRole, language);

  const { availableEmployees, fetchAvailableEmployees } = useEmployees(userRole);

  const styles = getGroupStyles({ theme, language, screenSize, spacing, fonts });
  const t = text[language];

  useEffect(() => {
    fetchMemberOfGroups();
  }, []);

  // Show the page skeleton only until the first successful load — later
  // `loading` toggles (create/assign actions) won't re-trigger the skeleton.
  const [pageReady, setPageReady] = useState(false);
  useEffect(() => {
    if (!loading) setPageReady(true);
  }, [loading]);

  useEffect(() => {
    if (!profile) return;
    const role = profile.role;
    setUserRole(role);
    if (role === "admin" || role === "manager") {
      fetchAllGroups();
    }
  }, [profile]);

  useEffect(() => {
    if (activeTab === "member") {
      fetchMemberOfGroups();
    } else if (activeTab === "created" && (userRole === "admin" || userRole === "manager")) {
      fetchCreatedGroups();
    }
  }, [activeTab]);

  const getFilteredGroups = () => {
    const dataSource = activeTab === "member" ? groups : createdGroups;
    if (!Array.isArray(dataSource)) return [];

    return dataSource.filter(group =>
      group.groupName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (group.creatorSr && group.creatorSr.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  };

  if (!pageReady && loading) {
    const isRTL = language === "ar";
    return (
      <div style={styles.container}>
        <SkeletonStyles theme={theme} />
        <LoadingPill
          text={isRTL ? "جارٍ تحميل الجروبات..." : "Loading groups information..."}
          theme={theme}
          isRTL={isRTL}
        />

        {/* Header skeleton */}
        <div
          style={{
            background: theme === "dark" ? "rgba(255,255,255,0.02)" : "rgba(255,255,255,0.9)",
            padding: "32px",
            borderRadius: "24px",
            border: theme === "dark" ? "1px solid rgba(255,255,255,0.15)" : "1px solid rgba(0,0,0,0.05)",
            marginBottom: "22px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            flexWrap: "wrap",
            gap: "24px",
            direction: isRTL ? "rtl" : "ltr",
          }}
        >
          <SkeletonBox width="220px" height="30px" />
          <div style={{ display: "flex", gap: "12px" }}>
            <SkeletonBox width="150px" height="44px" style={{ borderRadius: "12px" }} />
            <SkeletonBox width="180px" height="44px" style={{ borderRadius: "12px" }} />
          </div>
        </div>

        {/* Tabs skeleton */}
        <div style={styles.tabContainer}>
          <SkeletonBox width="140px" height="40px" style={{ borderRadius: "10px" }} />
          <SkeletonBox width="140px" height="40px" style={{ borderRadius: "10px" }} />
        </div>

        {/* Groups grid skeleton */}
        <div style={styles.groupsGrid}>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <SkeletonBox key={i} width="100%" height="150px" style={{ borderRadius: "18px" }} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <AlertModal
        open={!!error}
        title={language === 'ar' ? "خطأ" : "Error"}
        message={error}
        variant="error"
        onClose={() => setError("")}
        theme={theme}
        language={language}
      />
      <AlertModal
        open={!!success}
        title={language === 'ar' ? "نجاح" : "Success"}
        message={success}
        variant="success"
        onClose={() => setSuccess("")}
        theme={theme}
        language={language}
      />


      {/* Header */}
      <style>{`
        @keyframes slideInDown {
          from { opacity: 0; transform: translateY(-20px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
      <div
        style={{
          background: theme === "dark" ? "rgba(255,255,255,0.02)" : "rgba(255,255,255,0.9)",
          padding: "32px",
          borderRadius: "24px",
          border: theme === "dark" ? "1px solid rgba(255,255,255,0.15)" : "1px solid rgba(0,0,0,0.05)",
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
          boxShadow: theme === "dark" ? "0 8px 32px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.1)" : "0 8px 32px rgba(0,0,0,0.1)",
          animation: "slideInDown 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)",
          transition: "all 0.3s ease",
          marginBottom: "22px",
          marginTop: "-36px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          flexWrap: "wrap",
          gap: "24px",
          direction: language === "ar" ? "rtl" : "ltr",
        }}
      >
        <div>
          <h1
            style={{
              margin: 0,
              fontSize: "28px",
              fontWeight: 700,
              color: theme === "dark" ? "#f1f5f9" : "#1e293b",
              letterSpacing: "-0.5px",
            }}
          >
            {t.title}
          </h1>
          {/* {(userRole === "admin" || userRole === "manager") && (
            <p style={{
              margin: "12px 0 0 0",
              fontSize: "15px",
              fontWeight: 500,
              color: theme === "dark" ? "#cbd5e1" : "#64748b",
            }}>{t.subtitle}</p>
          )} */}
        </div>

        <div style={styles.buttonGroup}>
          {(userRole === "admin" || userRole === "manager" || isAdminViewGated) && canCreateGroup && (
              <button
                style={{
                  background: "linear-gradient(135deg, #2e7cba, #2e7cba)",
                  color: "#fff",
                  border: "none",
                  borderRadius: "12px",
                  padding: "12px 20px",
                  fontSize: "14px",
                  fontWeight: 700,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  boxShadow: "0 4px 14px 0 rgba(37, 99, 235, 0.24)",
                  transition: "all 0.3s ease",
                  whiteSpace: "nowrap",
                }}
                onClick={() => setShowCreateModal(true)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow = "0 6px 20px rgba(46,124,186,0.4)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "0 4px 14px 0 rgba(37, 99, 235, 0.24)";
                }}
              >
                <Plus size={20} />
                {t.createGroup}
              </button>
          )}

          {(userRole === "admin" || userRole === "manager" || isAdminViewGated) && canAssignEmployees && (
              <button
                style={{
                  background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                  color: "#fff",
                  border: "none",
                  borderRadius: "12px",
                  padding: "12px 20px",
                  fontSize: "14px",
                  fontWeight: 700,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  boxShadow: "0 4px 14px 0 rgba(16, 185, 129, 0.24)",
                  transition: "all 0.3s ease",
                  whiteSpace: "nowrap",
                }}
                onClick={() => {
                  fetchAvailableEmployees();
                  setShowAssignModal(true);
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow = "0 6px 20px rgba(16, 185, 129, 0.4)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "0 4px 14px 0 rgba(16, 185, 129, 0.24)";
                }}
              >
                <UserPlus size={20} />
                {t.assignEmployees}
              </button>
          )}
        </div>
      </div>
      

      {/* Search */}
      {/* <div style={styles.searchContainer}>
        <div style={{ position: 'relative', marginBottom: '-1rem' , marginTop: '-1rem', marginLeft: '-1rem' }}>
          <style>{`
            input[data-search="group-search"]::placeholder { color: ${theme === 'dark' ? "rgba(255,255,255,0.7)" : "#0f172a"}; opacity: 0.5; }
          `}</style>
          <Search
            size={18}
            style={{
              position: 'absolute',
              left: '1.5rem',
              marginLeft: '-0.75rem',
              top: '50%',
              transform: 'translateY(-50%)',
              opacity: 0.5
            }}
          />
          <input
            type="text"
            placeholder={t.search}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            data-search="group-search"
            style={{ ...styles.searchInput, paddingLeft: '3rem', }}
          />
        </div>
      </div> */}

      {/* Tabs */}
      <div style={styles.tabContainer}>
        <button
          style={{
            ...styles.tab,
            ...(activeTab === "member" ? styles.activeTab : {}),
            ...(hoveredTab === "member" && activeTab !== "member" ? {
              background: theme === "dark"
                ? "rgba(255,255,255,0.08)"
                : "rgba(0,0,0,0.04)",
              color: theme === "dark"
                ? "rgba(255,255,255,0.75)"
                : "rgba(26,26,26,0.8)",
              transform: "translateY(-2px)",
            } : {}),
          }}
          onClick={() => setActiveTab("member")}
          onMouseEnter={() => setHoveredTab("member")}
          onMouseLeave={() => setHoveredTab(null)}
        >
          {t.memberOfGroups}
        </button>
        {(userRole === "admin" || userRole === "manager") && (
          <button
            style={{
              ...styles.tab,
              ...(activeTab === "created" ? styles.activeTab : {}),
              ...(hoveredTab === "created" && activeTab !== "created" ? {
                background: theme === "dark"
                  ? "rgba(255,255,255,0.08)"
                  : "rgba(0,0,0,0.04)",
                color: theme === "dark"
                  ? "rgba(255,255,255,0.75)"
                  : "rgba(26,26,26,0.8)",
                transform: "translateY(-2px)",
              } : {}),
            }}
            onClick={() => setActiveTab("created")}
            onMouseEnter={() => setHoveredTab("created")}
            onMouseLeave={() => setHoveredTab(null)}
          >
            {t.createdGroups}
          </button>
        )}
      </div>

      {/* Groups Grid */}
      {loading ? (
        <div style={styles.emptyState}>
          <div style={{ fontSize: fonts.subtitle }}>{t.loading}</div>
        </div>
      ) : getFilteredGroups().length === 0 ? (
        <div style={styles.emptyState}>
          <Users size={64} style={{ opacity: 0.3, marginBottom: "1rem" }} />
          <div style={{ fontSize: fonts.subtitle, fontWeight: "600" }}>
            {activeTab === "member" ? t.noGroups : t.noCreatedGroups}
          </div>
          <div style={{ fontSize: fonts.body, marginTop: "0.5rem" }}>
            {activeTab === "member" ? t.noGroupsMessage : t.noCreatedGroupsMessage}
          </div>
        </div>
      ) : (
        <div style={styles.groupsGrid}>
          {getFilteredGroups().map((group) => (
            <GroupCard
              key={group.groupId || group.id}
              group={group}
              activeTab={activeTab}
              theme={theme}
              fonts={fonts}
              styles={styles}
              onClick={
                (userRole === "admin" || userRole === "manager") 
                  ? () => setSelectedGroupDetails(group) 
                  : undefined
              }
            />
          ))}
        </div>
      )}

      {/* Create Group Modal */}
      {showCreateModal && (
        <CreateGroupModal
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreateGroup}
          loading={loading}
          text={t}
          styles={styles}
          spacing={spacing}
        />
      )}

      {/* Assign Employees Modal */}
      {showAssignModal && (
        <AssignModal
          onClose={() => setShowAssignModal(false)}
          onSubmit={handleAssignEmployees}
          loading={loading}
          text={t}
          styles={styles}
          spacing={spacing}
          fonts={fonts}
          allGroups={allGroups}
          groups={groups}
          userRole={userRole}
          availableEmployees={availableEmployees}
        />
      )}

      {/* Group Details Modal */}
      {selectedGroupDetails && (
        <GroupDetailsModal
          group={selectedGroupDetails}
          onClose={() => setSelectedGroupDetails(null)}
          theme={theme}
          fonts={fonts}
          styles={styles}
          language={language}
          text={t}
          fetchGroupMembers={fetchGroupMembers}
        />
      )}
    </div>
  );
}

export default GroupsManagement;